import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowLeft, Music2, Search, Plus, Trash2, Download, ExternalLink,
  Lock, Save, Loader2, Heart, DoorOpen, Ban, Sparkles,
} from "lucide-react";
import { api, API, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import MyEventSidebar from "./MyEventSidebar";

const TECHNICAL_RE = /^(test[_\s-]|iter\d+|fixture|demo[_\s-]|debug|__)/i;
const cleanName = (s) => {
  if (!s) return "Votre mariage";
  const c = String(s).replace(/^\s*(test[_\s-]+|demo[_\s-]+)/i, "").replace(/\s+[a-f0-9]{6,}$/i, "").trim();
  return TECHNICAL_RE.test(c) ? "Votre mariage" : c;
};
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso || ""; }
};
const fmtDur = (ms) => {
  if (!ms) return "";
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export default function MyEventMusic() {
  const { eventId } = useParams();
  const { user } = useAuth();

  const [d, setD] = useState(null);          // full server payload
  const [must, setMust] = useState("");      // textarea (one title per line)
  const [donot, setDonot] = useState("");
  const [firstDance, setFirstDance] = useState("");
  const [entrance, setEntrance] = useState("");
  const [notes, setNotes] = useState("");
  const [tracks, setTracks] = useState([]);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const trackIds = useMemo(() => new Set(tracks.map(t => t.id)), [tracks]);

  // Load
  useEffect(() => {
    if (!eventId) return;
    api.get(`/next/myevent/${eventId}/music`)
      .then(r => {
        const p = r.data.playlist || {};
        setD(r.data);
        setMust((p.must_play || []).join("\n"));
        setDonot((p.do_not_play || []).join("\n"));
        setFirstDance(p.first_dance || "");
        setEntrance(p.entrance_song || "");
        setNotes(p.notes || "");
        setTracks(p.spotify_tracks || []);
      })
      .catch(e => setErr(e?.response?.status || 500));
  }, [eventId]);

  if (!user) return <Navigate to="/connexion" replace />;
  if (err === 403) return <Err title="Accès refusé" text="Vous n'êtes pas membre de cet événement." testid="music-forbidden" />;
  if (err === 404) return <Err title="Événement introuvable" text="Cet événement n'existe pas." testid="music-notfound" />;
  if (!d) return <div className="min-h-screen bg-black text-white p-8"><p className="text-white/50 italic">Chargement…</p></div>;

  const ev = d.event || {};
  const lock = d.lock || {};
  const isLocked = !!lock.is_locked;
  const couple = cleanName(ev.name);
  const questionnaire = d.questionnaire || {};
  const totalTracks = tracks.length;

  const doSearch = async () => {
    if (!q.trim() || isLocked) return;
    setSearching(true);
    try {
      const r = await api.get(`/next/myevent/${eventId}/music/spotify/search`,
        { params: { q: q.trim(), limit: 10 } });
      setResults(r.data?.tracks || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recherche Spotify indisponible.");
      setResults([]);
    } finally { setSearching(false); }
  };

  const addTrack = (t) => {
    if (isLocked) return;
    if (trackIds.has(t.id)) { toast.info("Déjà dans la playlist."); return; }
    setTracks(prev => [...prev, { ...t, category: "must_play", note: "" }]);
  };
  const removeTrack = (id) => {
    if (isLocked) return;
    setTracks(prev => prev.filter(t => t.id !== id));
  };

  const save = async () => {
    if (isLocked) return;
    setSaving(true);
    try {
      const payload = {
        must_play: must.split("\n").map(s => s.trim()).filter(Boolean),
        do_not_play: donot.split("\n").map(s => s.trim()).filter(Boolean),
        first_dance: firstDance.trim(),
        entrance_song: entrance.trim(),
        notes: notes.trim(),
        spotify_tracks: tracks,
      };
      await api.put(`/next/myevent/${eventId}/music`, payload);
      toast.success("Playlist enregistrée.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Enregistrement impossible.";
      toast.error(detail);
    } finally { setSaving(false); }
  };

  const openBlob = async (path, filename, mediaType) => {
    try {
      const token = getToken();
      const r = await fetch(`${API}/next/myevent/${eventId}/music/${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (filename) {
        const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      } else {
        window.open(url, "_blank");
      }
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Téléchargement impossible."); }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-20" data-testid="myevent-music">
      <header className="border-b border-[#DCB457]/15 bg-black/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Link to={`/myevent/${eventId}`} data-testid="music-back" className="text-xs uppercase tracking-widest text-white/60 hover:text-[#DCB457] inline-flex items-center gap-2">
            <ArrowLeft size={13} /> MyEvent
          </Link>
          <span className="text-[10px] uppercase tracking-[0.24em] text-[#DCB457] border border-[#DCB457]/40 px-2 py-1">MyMusic</span>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-8 lg:pt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <MyEventSidebar />
        <main className="lg:col-span-9 min-w-0 space-y-8">
          {/* Hero */}
          <section>
            <p className="eyebrow">MyEvent · Module</p>
            <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Votre <span className="italic gold-text">ambiance musicale.</span></h1>
            <p className="mt-3 text-white/60 text-sm md:text-base">Construisez la playlist qui portera chaque instant de votre mariage.</p>
            {ev.date && <p className="mt-1 text-[#DCB457] text-sm" data-testid="music-event-summary">{couple} — {fmtDate(ev.date)}</p>}
          </section>

          {/* Statut + lock */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="music-status">
            <Kpi label="Morceaux" value={totalTracks} accent />
            <Kpi label="Titres à jouer" value={must.split("\n").filter(Boolean).length} />
            <Kpi label="À éviter" value={donot.split("\n").filter(Boolean).length} />
            <Kpi
              label={isLocked ? "Verrouillée" : "Clôture"}
              value={lock.lock_deadline ? fmtDate(lock.lock_deadline) : "—"}
              warn={isLocked}
            />
          </section>

          {isLocked && (
            <div className="card-luxe p-5 border-amber-400/40 flex items-start gap-3" data-testid="music-lock-banner">
              <Lock size={18} className="text-amber-300 mt-0.5" />
              <div>
                <p className="font-serif-luxe text-lg">Playlist verrouillée depuis le {fmtDate(lock.lock_deadline)}</p>
                <p className="text-white/60 text-sm mt-1">
                  Clôture automatique 15 jours avant votre mariage pour préparer le déroulé. Pour toute modification de dernière minute, contactez My Animation directement.
                </p>
              </div>
            </div>
          )}

          {/* Moments clés */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="music-moments">
            <MomentField
              label="Entrée des mariés"
              icon={DoorOpen}
              value={entrance}
              onChange={setEntrance}
              disabled={isLocked}
              testid="music-entrance"
              placeholder="Ex. Kygo — Higher Love"
            />
            <MomentField
              label="Première danse"
              icon={Heart}
              value={firstDance}
              onChange={setFirstDance}
              disabled={isLocked}
              testid="music-first-dance"
              placeholder="Ex. Ed Sheeran — Perfect"
            />
          </section>

          {/* Recherche Spotify */}
          <section data-testid="music-search-section">
            <p className="eyebrow flex items-center gap-2"><Search size={11} /> Recherche Spotify</p>
            <div className="card-luxe p-5 mt-3">
              <div className="flex flex-col md:flex-row gap-3">
                <input
                  data-testid="music-search-input"
                  disabled={isLocked}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && doSearch()}
                  placeholder="Titre, artiste, album…"
                  className="flex-1 bg-black/50 border border-[#DCB457]/25 focus:border-[#DCB457] outline-none px-4 py-2.5 text-sm text-white placeholder-white/30 disabled:opacity-50"
                />
                <button
                  data-testid="music-search-btn"
                  disabled={isLocked || searching || !q.trim()}
                  onClick={doSearch}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#DCB457] text-black hover:bg-[#DCB457]/90 text-xs uppercase tracking-widest disabled:opacity-40"
                >
                  {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Rechercher
                </button>
              </div>
              {results.length > 0 && (
                <div className="mt-4 divide-y divide-[#DCB457]/10" data-testid="music-search-results">
                  {results.map(t => (
                    <div key={t.id} className="flex items-center gap-3 py-2.5">
                      {t.album_image
                        ? <img src={t.album_image} alt="" className="w-11 h-11 object-cover" />
                        : <div className="w-11 h-11 bg-white/5 flex items-center justify-center"><Music2 size={16} className="text-white/30" /></div>}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white/90 truncate">{t.name}</p>
                        <p className="text-[11px] text-white/50 truncate">{t.artists} · {fmtDur(t.duration_ms)}</p>
                      </div>
                      <button
                        data-testid={`music-add-${t.id}`}
                        disabled={isLocked || trackIds.has(t.id)}
                        onClick={() => addTrack(t)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#DCB457]/40 text-[#DCB457] text-[11px] uppercase tracking-widest hover:bg-[#DCB457]/10 disabled:opacity-40"
                      >
                        <Plus size={12} /> {trackIds.has(t.id) ? "Ajouté" : "Ajouter"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Morceaux sélectionnés */}
          <section data-testid="music-selected-section">
            <p className="eyebrow flex items-center gap-2"><Sparkles size={11} /> Vos morceaux sélectionnés ({totalTracks})</p>
            <div className="card-luxe mt-3">
              {tracks.length === 0 ? (
                <p className="p-6 text-white/45 italic text-sm">Aucun morceau ajouté pour l'instant. Utilisez la recherche ci-dessus.</p>
              ) : (
                <div className="divide-y divide-[#DCB457]/10">
                  {tracks.map(t => (
                    <div key={t.id} data-testid={`music-track-${t.id}`} className="flex items-center gap-3 px-4 py-2.5">
                      {t.album_image
                        ? <img src={t.album_image} alt="" className="w-10 h-10 object-cover" />
                        : <div className="w-10 h-10 bg-white/5 flex items-center justify-center"><Music2 size={14} className="text-white/30" /></div>}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white/90 truncate">{t.name}</p>
                        <p className="text-[11px] text-white/50 truncate">{t.artists}</p>
                      </div>
                      {t.external_url && (
                        <a href={t.external_url} target="_blank" rel="noreferrer" className="text-white/40 hover:text-[#DCB457]"><ExternalLink size={13} /></a>
                      )}
                      <button
                        data-testid={`music-remove-${t.id}`}
                        disabled={isLocked}
                        onClick={() => removeTrack(t.id)}
                        className="text-white/40 hover:text-red-300 disabled:opacity-30"
                      ><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Must play / Do not play */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="music-lists">
            <ListField
              label="Titres à jouer (must play)"
              icon={Sparkles}
              value={must}
              onChange={setMust}
              disabled={isLocked}
              testid="music-must-play"
              placeholder="Un titre par ligne&#10;Ex. Stromae — Papaoutai"
              hint="Séparez chaque morceau par une nouvelle ligne."
            />
            <ListField
              label="Titres à éviter (do not play)"
              icon={Ban}
              value={donot}
              onChange={setDonot}
              disabled={isLocked}
              testid="music-do-not-play"
              placeholder="Un titre par ligne"
              hint="Ces titres ne seront jamais joués."
            />
          </section>

          {/* Notes + questionnaire */}
          <section data-testid="music-notes-section">
            <p className="eyebrow">Notes & ambiance</p>
            <div className="card-luxe p-5 mt-3 space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/50">Notes libres pour le DJ</label>
                <textarea
                  data-testid="music-notes"
                  disabled={isLocked}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  className="w-full mt-2 bg-black/50 border border-[#DCB457]/25 focus:border-[#DCB457] outline-none px-3 py-2 text-sm text-white/90 disabled:opacity-50"
                  placeholder="Ambiance générale, souhaits particuliers, moments-clés…"
                />
              </div>
              {(questionnaire.style_musical || questionnaire.ambiance_souhaitee) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-[#DCB457]/10">
                  {questionnaire.style_musical && (
                    <div data-testid="music-questionnaire-style">
                      <p className="text-[10px] uppercase tracking-widest text-white/40">Style musical (questionnaire)</p>
                      <p className="text-sm text-white/80 mt-1">{questionnaire.style_musical}</p>
                    </div>
                  )}
                  {questionnaire.ambiance_souhaitee && (
                    <div data-testid="music-questionnaire-ambiance">
                      <p className="text-[10px] uppercase tracking-widest text-white/40">Ambiance souhaitée</p>
                      <p className="text-sm text-white/80 mt-1">{questionnaire.ambiance_souhaitee}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Actions */}
          <section className="flex flex-wrap gap-3 border-t border-[#DCB457]/15 pt-6" data-testid="music-actions">
            <button
              data-testid="music-save"
              disabled={saving || isLocked}
              onClick={save}
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#DCB457] text-black hover:bg-[#DCB457]/90 text-xs uppercase tracking-widest disabled:opacity-40"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
            <button
              data-testid="music-pdf"
              onClick={() => openBlob("pdf", "mymusic-playlist.pdf")}
              className="inline-flex items-center gap-2 px-5 py-3 border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10 text-xs uppercase tracking-widest"
            >
              <Download size={14} /> Exporter PDF
            </button>
            <button
              data-testid="music-spotify-html"
              onClick={() => openBlob("spotify/html", null, "text/html")}
              className="inline-flex items-center gap-2 px-5 py-3 border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10 text-xs uppercase tracking-widest"
            >
              <ExternalLink size={14} /> One-click Spotify
            </button>
          </section>
        </main>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent, warn }) {
  return (
    <div className="card-luxe p-4">
      <p className="eyebrow">{label}</p>
      <p className={`font-serif-luxe text-2xl mt-2 ${accent ? "gold-text" : warn ? "text-amber-300" : ""}`}>{value}</p>
    </div>
  );
}

function MomentField({ label, icon: Icon, value, onChange, disabled, testid, placeholder }) {
  return (
    <div className="card-luxe p-5">
      <p className="text-[11px] uppercase tracking-widest text-white/50 flex items-center gap-2">
        <Icon size={12} /> {label}
      </p>
      <input
        data-testid={testid}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full mt-2 bg-black/50 border border-[#DCB457]/25 focus:border-[#DCB457] outline-none px-3 py-2 text-sm text-white/90 disabled:opacity-50"
      />
    </div>
  );
}

function ListField({ label, icon: Icon, value, onChange, disabled, testid, placeholder, hint }) {
  return (
    <div className="card-luxe p-5">
      <p className="text-[11px] uppercase tracking-widest text-white/50 flex items-center gap-2">
        <Icon size={12} /> {label}
      </p>
      <textarea
        data-testid={testid}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        placeholder={placeholder}
        className="w-full mt-2 bg-black/50 border border-[#DCB457]/25 focus:border-[#DCB457] outline-none px-3 py-2 text-sm text-white/90 disabled:opacity-50 whitespace-pre-wrap"
      />
      {hint && <p className="text-[10px] text-white/40 italic mt-1">{hint}</p>}
    </div>
  );
}

function Err({ title, text, testid }) {
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
      <div className="max-w-md text-center" data-testid={testid}>
        <p className="eyebrow">MyMusic</p>
        <h1 className="font-serif-luxe text-3xl mt-3 mb-3">{title}</h1>
        <p className="text-white/60 text-sm">{text}</p>
      </div>
    </div>
  );
}
